// src/App.js
import React, { useEffect, useState, useRef } from "react";
import * as Sentry from "@sentry/react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { SignedIn, SignedOut, useAuth, useClerk } from "@clerk/clerk-react";

// ✅ NEW: Global notifications provider
import { NotificationsProvider } from "./components/notifications/NotificationsProvider";

// ✅ NEW: Promo modal (user-facing)
import PromoModal from "./components/PromoModal";

// Billing
import BillingSuccess from "./pages/BillingSuccess";
import BillingCancel from "./pages/BillingCancel";
import Pricing from "./pages/Pricing";

// Layout
import SidebarLayout from "./components/SidebarLayout";
import Header from "./components/Header";

// Public Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login"; // Custom login page
import Signup from "./pages/Signup"; // Custom signup page

// Dashboard Sections
import Dashboard from "./pages/Dashboard";
import Generate from "./pages/Generate";
import Analyze from "./pages/Analyze-Products";
import Competition from "./pages/Competition";

// Project Tabs
import ProjectDetails from "./pages/ProjectDetails";
import ProjectOverview from "./pages/ProjectOverview";
import AdCreatives from "./pages/AdCreatives";
import AdMarketingVideos from "./pages/AdMarketingVideos";
import PhotoshootImages from "./pages/PhotoshootImages";
import RemoveBackground from "./pages/RemoveBackground";
import ProductAvatarImages from "./pages/ProductAvatarImages";
import ProductAvatarVideos from "./pages/ProductAvatarVideos";
import PhotoshootAnyshootImages from "./pages/PhotoshootAnyshootImages";

/** ---- New Phot.AI integrations ---- */
import ColorRestoration from "./pages/ColorRestoration";
import Enhancer from "./pages/Enhancer";
import Outpaint from "./pages/Outpaint";
import LightFix from "./pages/LightFix";
import ObjectReplacer from "./pages/ObjectReplacer";
import OldPhotosRestoration from "./pages/OldPhotosRestoration";
import Upscaler from "./pages/Upscaler";

/** ---- Background Replacer (NEW) ---- */
import BackgroundReplacer from "./pages/BackgroundReplacer";

/** ---- Object Remover (NEW) ---- */
import ObjectRemover from "./pages/ObjectRemover";

/** ---- Admin Backoffice (NEW) ---- */
import AdminUsers from "./pages/AdminUsers";
import AdminUserDetail from "./pages/AdminUserDetail";

// ✅ NEW: Admin Landing editor page
import AdminLanding from "./pages/AdminLanding";

// ✅ NEW: Admin Promo Modal page
import AdminPromoModal from "./pages/AdminPromoModal";

/** ---- Legal / Policy pages (NEW) ---- */
import TermsConditions from "./pages/TermsConditions";
import RefundPolicy from "./pages/RefundPolicy";
import PrivacyPolicy from "./pages/PrivacyPolicy";

/* ────────────────────────────────────────────────────────────────────
   SENTRY (Frontend)
   - CRA env var: REACT_APP_SENTRY_DSN
   - Optional:
     REACT_APP_SENTRY_TRACES_SAMPLE_RATE (default 0.2)
     REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE (default 0.0)
     REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE (default 1.0)
   - We init at module load (earliest we can from this file).
   ──────────────────────────────────────────────────────────────────── */
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

if (
  SENTRY_DSN &&
  typeof window !== "undefined" &&
  !window.__PIXELADS_SENTRY_INIT__
) {
  window.__PIXELADS_SENTRY_INIT__ = true;

  const tracesSampleRate = Number(
    process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || 0.2
  );
  const replaysSessionSampleRate = Number(
    process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || 0.0
  );
  const replaysOnErrorSampleRate = Number(
    process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || 1.0
  );

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV,

    integrations: [
      // React Router v6 navigation tracing
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      // Session Replay
      Sentry.replayIntegration(),
    ],

    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.2,

    // Make sure auth/API calls propagate tracing where useful
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/ecom-ads-ai-production\.up\.railway\.app/i,
      /^https:\/\/.*\.railway\.app/i,
      /^https:\/\/(www\.)?pixelads\.ai/i,
    ],

    // Replay sampling
    replaysSessionSampleRate: Number.isFinite(replaysSessionSampleRate)
      ? replaysSessionSampleRate
      : 0.0,
    replaysOnErrorSampleRate: Number.isFinite(replaysOnErrorSampleRate)
      ? replaysOnErrorSampleRate
      : 1.0,
  });
}

/* ✅ 2.3: Wrap React Router v6 Routes for Sentry instrumentation */
const SentryRoutes = SENTRY_DSN
  ? Sentry.withSentryReactRouterV6Routing(Routes)
  : Routes;

/* ────────────────────────────────────────────────────────────────────
   AdminGate: prevents any rendering of admin UI for non-superadmins.
   It calls /api/admin/__ping (guarded by requireAdmin). If 404/403,
   we immediately redirect and render nothing (no header/sidebar flash).
   ──────────────────────────────────────────────────────────────────── */
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window._API_BASE) ||
  "https://ecom-ads-ai-production.up.railway.app";

/* ────────────────────────────────────────────────────────────────────
   ✅ NEW: Best-effort sync to backend (which also upserts SendGrid contact)
   - Calls POST /api/auth/sync ONCE per signed-in session
   - Requires Clerk token
   - Silent failure (does not block UI)
   ──────────────────────────────────────────────────────────────────── */
function useSendgridUserSyncOnce() {
  const { isSignedIn, userId, getToken } = useAuth();
  const didRunRef = useRef(false);

  useEffect(() => {
    if (!isSignedIn || !userId) {
      didRunRef.current = false;
      return;
    }

    if (didRunRef.current) return;
    didRunRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;

        await fetch(`${API_BASE}/api/auth/sync`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }).catch(() => {});
      } catch (e) {
        if (SENTRY_DSN) Sentry.captureException(e);
        // best-effort: no UI impact
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, userId, getToken]);
}

function AdminGate({ children }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(null); // null=pending, true=ok, false=deny

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        if (!token) {
          if (!cancelled) {
            setAllowed(false);
            navigate("/", { replace: true });
          }
          return;
        }
        const res = await fetch(`${API_BASE}/api/admin/__ping`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        if (!cancelled) {
          if (res.ok) setAllowed(true);
          else {
            setAllowed(false);
            // Hide existence of admin: redirect to home (or /dashboard)
            navigate("/", { replace: true });
          }
        }
      } catch (e) {
        // Sentry capture (keeps behavior unchanged)
        if (SENTRY_DSN) Sentry.captureException(e);

        if (!cancelled) {
          setAllowed(false);
          navigate("/", { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, navigate]);

  if (allowed !== true) return null; // nothing while checking or when denied
  return children;
}

/* ────────────────────────────────────────────────────────────────────
   AccessBlocker: calls /api/access/status (mounted outside blocked guard).
   If backend reports blocked, we show a hard, full-screen modal that
   prevents any interaction and offers a “Log out” button.
   ──────────────────────────────────────────────────────────────────── */
function AccessBlocker() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const location = useLocation();

  const [checked, setChecked] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [reason, setReason] = useState("");

  const check = React.useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) {
        setBlocked(false);
        setChecked(true);
        return;
      }
      const res = await fetch(`${API_BASE}/api/access/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const j = await res.json().catch(() => ({}));
      // Backend returns { ok: true, is_blocked: boolean, ... }
      setBlocked(!!j?.is_blocked);
      setReason(j?.reason || "");
      setChecked(true);
    } catch (e) {
      // Sentry capture (keeps behavior unchanged)
      if (SENTRY_DSN) Sentry.captureException(e);

      // Don't brick the UI on network hiccups
      setBlocked(false);
      setChecked(true);
    }
  }, [getToken]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!cancel) await check();
    })();
    // Poll so block/unblock applies without a full reload
    const id = setInterval(check, 15000);
    return () => {
      cancel = true;
      clearInterval(id);
    };
  }, [check, location.pathname]);

  if (!checked || !blocked) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: "rgba(12,12,26,0.92)" }}
      aria-modal="true"
      role="dialog"
    >
      <div className="mx-4 w-full max-w-md rounded-2xl border border-rose-700/40 bg-[#141428] p-5 text-center">
        <div className="text-lg font-semibold text-zinc-100">
          Access Restricted
        </div>
        <p className="mt-2 text-sm text-zinc-300">
          {reason ||
            "You have been banned from using the product. Please contact support@pixelads.ai for more information."}
        </p>
        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            onClick={() => signOut({ redirectUrl: "/" })}
            className="px-4 py-2 rounded-lg border border-zinc-700/60 text-zinc-200 hover:border-pink-600/60 hover:text-pink-300"
          >
            Log out
          </button>
        </div>
      </div>

      {/* Click shield to block all underlying interactions */}
      <div className="absolute inset-0 pointer-events-auto" />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   ✅ PromoModalGate: fetches /api/promo-modal and shows overlay if needed.
   - On close, calls POST /api/promo-modal/dismiss
   ──────────────────────────────────────────────────────────────────── */
function PromoModalGate() {
  const { getToken, isSignedIn } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState({
    active: false,
    version: 1,
    title: "",
    description: "",
    imageUrl: "",
  });

  const didLoadRef = useRef(false);

  useEffect(() => {
    if (!isSignedIn) {
      didLoadRef.current = false;
      setOpen(false);
      return;
    }

    if (didLoadRef.current) return;
    didLoadRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;

        const res = await fetch(`${API_BASE}/api/promo-modal`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        const j = await res.json().catch(() => ({}));
        if (cancelled) return;

        const c = j?.config || {};
        setCfg({
          active: !!c.active,
          version: Number(c.version || 1) || 1,
          title: String(c.title || ""),
          description: String(c.description || ""),
          imageUrl: String(c.imageUrl || ""),
        });

        setOpen(!!j?.show);
      } catch (e) {
        if (SENTRY_DSN) Sentry.captureException(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken, isSignedIn]);

  const dismiss = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      if (!token) {
        setOpen(false);
        return;
      }

      await fetch(`${API_BASE}/api/promo-modal/dismiss`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ version: cfg.version }),
      }).catch(() => {});
    } catch (e) {
      if (SENTRY_DSN) Sentry.captureException(e);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  }, [getToken, cfg.version]);

  return (
    <PromoModal
      open={open}
      title={cfg.title}
      description={cfg.description}
      imageUrl={cfg.imageUrl}
      onClose={dismiss}
      onPrimary={dismiss}
      primaryLabel="Got it"
      loading={loading}
    />
  );
}

function AppContent() {
  const location = useLocation();
  const { userId } = useAuth();

  // ✅ NEW: sync user -> backend -> SendGrid (best-effort, once)
  useSendgridUserSyncOnce();

  // Attach Clerk user id to Sentry scope
  useEffect(() => {
    if (!SENTRY_DSN) return;
    if (userId) Sentry.setUser({ id: userId });
    else Sentry.setUser(null);
  }, [userId]);

  // Hide header on billing pages
  const hideHeader =
    location.pathname.startsWith("/billing/success") ||
    location.pathname.startsWith("/billing/cancel");

  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="min-h-screen bg-gray-100 font-sans flex items-center justify-center p-6">
          <div className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-5 text-center">
            <div className="text-lg font-semibold text-zinc-900">
              Something went wrong
            </div>
            <p className="mt-2 text-sm text-zinc-600">
              Please refresh the page. If it happens again, we’ll investigate.
            </p>
            <div className="mt-4">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-lg border border-zinc-300 text-zinc-800 hover:border-zinc-400"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      }
    >
      <div className="min-h-screen bg-gray-100 font-sans">
        <SignedOut>
          <SentryRoutes>
            <Route path="/" element={<Landing />} />
            {/* Public auth routes – allow Clerk sub-paths like /login/verify */}
            <Route path="/login/*" element={<Login />} />
            <Route path="/signup/*" element={<Signup />} />

            {/* Public legal/policy pages */}
            <Route path="/terms" element={<TermsConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />

            <Route path="*" element={<Navigate to="/" />} />
          </SentryRoutes>
        </SignedOut>

        <SignedIn>
          {/* ✅ Notifications must wrap ALL logged-in UI so toasts work everywhere */}
          <NotificationsProvider apiBase={API_BASE}>
            {/* Global banned-user guard */}
            <AccessBlocker />

            {/* ✅ NEW: Promo modal gate (after login) */}
            <PromoModalGate />

            {!hideHeader && <Header />}

            <SentryRoutes>
              {/* Default logged-in route goes to /analyze */}
              <Route path="/" element={<Navigate to="/analyze" />} />

              {/* If a logged-in user hits /login or /signup, send them to /analyze */}
              <Route path="/login/*" element={<Navigate to="/analyze" />} />
              <Route path="/signup/*" element={<Navigate to="/analyze" />} />

              {/* Billing (no header) */}
              <Route path="/billing/success" element={<BillingSuccess />} />
              <Route path="/billing/cancel" element={<BillingCancel />} />

              {/* Pricing (header shown, NO sidebar) */}
              <Route path="/pricing" element={<Pricing />} />

              {/* Legal / policy pages (header, no sidebar) */}
              <Route path="/terms" element={<TermsConditions />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />

              {/* -------- Admin Backoffice (no sidebar) -------- */}
              {/* ✅ CHANGE: /admin now shows AdminLanding by default */}
              <Route
                path="/admin"
                element={
                  <AdminGate>
                    <AdminLanding />
                  </AdminGate>
                }
              />

              {/* ✅ Keep users list at /admin/users */}
              <Route
                path="/admin/users"
                element={
                  <AdminGate>
                    <AdminUsers />
                  </AdminGate>
                }
              />

              <Route
                path="/admin/users/:id"
                element={
                  <AdminGate>
                    <AdminUserDetail />
                  </AdminGate>
                }
              />

              {/* ✅ Optional alias: /admin/landing still works */}
              <Route
                path="/admin/landing"
                element={
                  <AdminGate>
                    <AdminLanding />
                  </AdminGate>
                }
              />

              {/* ✅ NEW: Admin Promo Modal editor */}
              <Route
                path="/admin/promo-modal"
                element={
                  <AdminGate>
                    <AdminPromoModal />
                  </AdminGate>
                }
              />
              {/* ---------------------------------------------- */}

              {/* Dashboard */}
              <Route
                path="/dashboard"
                element={
                  <SidebarLayout>
                    <Dashboard />
                  </SidebarLayout>
                }
              />

              {/* Generate */}
              <Route
                path="/generate"
                element={
                  <SidebarLayout>
                    <Generate />
                  </SidebarLayout>
                }
              />

              {/* ---- Product Image Photoshoot Anyshoot ---- */}
              <Route
                path="/generate/photoshoot-anyshoot"
                element={
                  <SidebarLayout>
                    <PhotoshootAnyshootImages />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/photoshoot-anyshoot/:projectId"
                element={
                  <SidebarLayout>
                    <PhotoshootAnyshootImages />
                  </SidebarLayout>
                }
              />

              {/* Old Photoshoot Images */}
              <Route
                path="/generate/photoshoot"
                element={
                  <SidebarLayout>
                    <PhotoshootImages />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/photoshoot/:projectId"
                element={
                  <SidebarLayout>
                    <PhotoshootImages />
                  </SidebarLayout>
                }
              />

              {/* Remove Background */}
              <Route
                path="/generate/remove-bg"
                element={
                  <SidebarLayout>
                    <RemoveBackground />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/remove-bg/:projectId"
                element={
                  <SidebarLayout>
                    <RemoveBackground />
                  </SidebarLayout>
                }
              />

              {/* ========= New Phot.AI Integrations ========= */}
              <Route
                path="/generate/color-restoration"
                element={
                  <SidebarLayout>
                    <ColorRestoration />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/color-restoration/:projectId"
                element={
                  <SidebarLayout>
                    <ColorRestoration />
                  </SidebarLayout>
                }
              />

              <Route
                path="/generate/enhancer"
                element={
                  <SidebarLayout>
                    <Enhancer />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/enhancer/:projectId"
                element={
                  <SidebarLayout>
                    <Enhancer />
                  </SidebarLayout>
                }
              />

              <Route
                path="/generate/outpaint"
                element={
                  <SidebarLayout>
                    <Outpaint />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/outpaint/:projectId"
                element={
                  <SidebarLayout>
                    <Outpaint />
                  </SidebarLayout>
                }
              />

              <Route
                path="/generate/light-fix"
                element={
                  <SidebarLayout>
                    <LightFix />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/light-fix/:projectId"
                element={
                  <SidebarLayout>
                    <LightFix />
                  </SidebarLayout>
                }
              />

              <Route
                path="/generate/object-replacer"
                element={
                  <SidebarLayout>
                    <ObjectReplacer />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/object-replacer/:projectId"
                element={
                  <SidebarLayout>
                    <ObjectReplacer />
                  </SidebarLayout>
                }
              />

              <Route
                path="/generate/object-remover"
                element={
                  <SidebarLayout>
                    <ObjectRemover />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/object-remover/:projectId"
                element={
                  <SidebarLayout>
                    <ObjectRemover />
                  </SidebarLayout>
                }
              />

              <Route
                path="/generate/old-photos-restoration"
                element={
                  <SidebarLayout>
                    <OldPhotosRestoration />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/old-photos-restoration/:projectId"
                element={
                  <SidebarLayout>
                    <OldPhotosRestoration />
                  </SidebarLayout>
                }
              />

              <Route
                path="/generate/upscaler"
                element={
                  <SidebarLayout>
                    <Upscaler />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/upscaler/:projectId"
                element={
                  <SidebarLayout>
                    <Upscaler />
                  </SidebarLayout>
                }
              />

              <Route
                path="/generate/background-replacer"
                element={
                  <SidebarLayout>
                    <BackgroundReplacer />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/background-replacer/:projectId"
                element={
                  <SidebarLayout>
                    <BackgroundReplacer />
                  </SidebarLayout>
                }
              />
              {/* ========= /New Phot.AI Integrations ========= */}

              {/* Ad Marketing Video */}
              <Route
                path="/generate/ad-marketing-video"
                element={
                  <SidebarLayout>
                    <AdMarketingVideos />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/ad-marketing-video/:projectId"
                element={
                  <SidebarLayout>
                    <AdMarketingVideos />
                  </SidebarLayout>
                }
              />

              {/* Ad Creatives */}
              <Route
                path="/generate/ad-creatives"
                element={
                  <SidebarLayout>
                    <AdCreatives />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/ad-creatives/:projectId"
                element={
                  <SidebarLayout>
                    <AdCreatives />
                  </SidebarLayout>
                }
              />

              {/* Product Avatar Image */}
              <Route
                path="/generate/product-avatar-image"
                element={
                  <SidebarLayout>
                    <ProductAvatarImages />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/product-avatar-image/:projectId"
                element={
                  <SidebarLayout>
                    <ProductAvatarImages />
                  </SidebarLayout>
                }
              />

              {/* Product Avatar Video */}
              <Route
                path="/generate/product-avatar-video"
                element={
                  <SidebarLayout>
                    <ProductAvatarVideos />
                  </SidebarLayout>
                }
              />
              <Route
                path="/generate/product-avatar-video/:projectId"
                element={
                  <SidebarLayout>
                    <ProductAvatarVideos />
                  </SidebarLayout>
                }
              />

              {/* Project */}
              <Route
                path="/project/:id"
                element={
                  <SidebarLayout>
                    <ProjectDetails />
                  </SidebarLayout>
                }
              />

              {/* Analyze + Competition */}
              <Route
                path="/analyze"
                element={
                  <SidebarLayout>
                    <Analyze />
                  </SidebarLayout>
                }
              />
              <Route
                path="/competition"
                element={
                  <SidebarLayout>
                    <Competition />
                  </SidebarLayout>
                }
              />

              {/* Dashboard → Project Overview (tabbed) */}
              <Route
                path="/dashboard/:projectId/*"
                element={
                  <SidebarLayout>
                    <ProjectOverview />
                  </SidebarLayout>
                }
              >
                <Route index element={<Navigate to="ad-images" replace />} />
                <Route path="avatar-videos" element={<AdMarketingVideos />} />
              </Route>

              {/* Fallback to /analyze */}
              <Route path="*" element={<Navigate to="/analyze" />} />
            </SentryRoutes>
          </NotificationsProvider>
        </SignedIn>
      </div>
    </Sentry.ErrorBoundary>
  );
}

/* ✅ 3.2: Wrap main app content with Sentry profiler */
const AppContentWithProfiler = SENTRY_DSN ? Sentry.withProfiler(AppContent) : AppContent;

export default function App() {
  return <AppContentWithProfiler />;
}
